import { useCallback } from 'react';
import type { SaveStatus } from '../components/app-home-v2/PreferencesDialog';
import { useLayoutPrefs } from './useLayoutPrefs';
import { usePrefSaveStatus } from './usePrefSaveStatus';

/**
 * Rep opt-in to Terra Beta (`terraDesignV2`), read side only. Gate future work
 * with `useTerraDesignV2Enabled()`; nothing is gated on it today. Wait for
 * `isLoaded` before choosing a branch, so a cold load never flashes the other
 * UI. Reads through `useLayoutPrefs` (one cache entry, `PREFS_KEY`), never a
 * second query on the same key.
 */
export function useTerraDesignV2Enabled(): { enabled: boolean; isLoaded: boolean } {
  const { terraDesignV2, prefsLoaded } = useLayoutPrefs();
  return { enabled: terraDesignV2, isLoaded: prefsLoaded };
}

/**
 * Rep opt-in to Terra Beta (`terraDesignV2`), write side.
 * Writes through `useLayoutPrefs`'s one mutation (`setTerraDesignV2`), so the
 * Preferences row's write side and any other caller share the same
 * optimistic-update semantics (`old ?? DEFAULTS`) rather than a second
 * `useMutation` racing the same cache entry. `saveStatus` comes from
 * `usePrefSaveStatus`.
 */
export function useSetTerraDesignV2(): { setEnabled: (next: boolean) => void; saveStatus?: SaveStatus } {
  const { setTerraDesignV2, saveStatus: mutationStatus, resetSaveStatus } = useLayoutPrefs();
  const saveStatus = usePrefSaveStatus(mutationStatus, resetSaveStatus);

  const setEnabled = useCallback((next: boolean) => setTerraDesignV2(next), [setTerraDesignV2]);

  return { setEnabled, saveStatus };
}
